import { useState } from 'react';
import { Button } from '../ui/button';
import { Loader2, Search, Shield } from 'lucide-react';

export default function BookingDiagnostics({ authAxios }) {
    var [html, setHtml] = useState('');
    var [loading, setLoading] = useState(false);
    var [recovering, setRecovering] = useState(false);
    var [deduping, setDeduping] = useState(false);

    function runDiag() {
        setLoading(true);
        authAxios.get('/admin/booking-diagnostic')
            .then(function(res) { setHtml(formatDiag(res.data)); setLoading(false); })
            .catch(function(e) { setHtml('<p class="text-red-600">Error: ' + e + '</p>'); setLoading(false); });
    }

    function runRecov() {
        if (!window.confirm('Recover missing bookings from payment records?')) return;
        setRecovering(true);
        authAxios.post('/admin/recover-bookings')
            .then(function(res) { setHtml(formatRecov(res.data)); setRecovering(false); })
            .catch(function(e) { setHtml('<p class="text-red-600">Recovery Error: ' + e + '</p>'); setRecovering(false); });
    }

    function runDedup() {
        if (!window.confirm('Find and remove duplicate bookings? This keeps the oldest booking per customer per session and removes extras.')) return;
        setDeduping(true);
        authAxios.post('/admin/bookings/dedup')
            .then(function(res) { setHtml(formatDedup(res.data)); setDeduping(false); })
            .catch(function(e) { setHtml('<p class="text-red-600">Dedup Error: ' + e + '</p>'); setDeduping(false); });
    }

    return (
        <div data-testid="booking-diagnostics">
            <div className="flex flex-wrap gap-3 mb-4">
                <Button onClick={runDiag} disabled={loading} data-testid="run-diagnostic-btn">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Run Booking Diagnostic
                </Button>
                <Button onClick={runRecov} disabled={recovering} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="run-recovery-btn">
                    {recovering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                    Recover Missing Bookings
                </Button>
                <Button onClick={runDedup} disabled={deduping} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="run-dedup-btn">
                    {deduping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Remove Duplicate Bookings
                </Button>
            </div>
            <div dangerouslySetInnerHTML={{ __html: html }} />
        </div>
    );
}

function formatDiag(d) {
    var h = '';
    h += '<div style="display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:16px">';
    h += box(d.total_cart_bookings, 'Cart Bookings', '#f3f4f6');
    h += box(d.total_stripe_events_completed, 'Stripe Paid', '#eff6ff');
    h += box(d.total_paid_transactions, 'Paid TXs', '#f0fdf4');
    h += box(d.total_safety_forms, 'Safety Forms', '#faf5ff');
    h += box(d.total_holds, 'Holds', '#f9fafb');
    h += '</div>';

    var months = d.monthly_breakdown ? Object.keys(d.monthly_breakdown).sort() : [];
    h += '<div style="border:1px solid #e5e7eb;border-radius:8px;padding:16px;margin-bottom:12px">';
    h += '<h3 style="font-weight:bold;margin-bottom:8px">Monthly Breakdown</h3>';
    if (months.length === 0) h += '<p style="color:#6b7280">No bookings found</p>';
    for (var i = 0; i < months.length; i++) {
        var m = months[i];
        var c = d.monthly_breakdown[m];
        h += '<div style="display:flex;justify-content:space-between;align-items:center;padding:6px 8px;background:#f9fafb;border-radius:4px;margin-bottom:4px">';
        h += '<span style="font-weight:500;font-size:14px">' + esc(m) + '</span>';
        h += '<span>';
        h += badge(c.cart_items + ' total', '#e5e7eb', '#111');
        if (c.confirmed > 0) h += badge(c.confirmed + ' confirmed', '#dcfce7', '#15803d');
        if (c.pending > 0) h += badge(c.pending + ' pending', '#fef3c7', '#b45309');
        if (c.cancelled > 0) h += badge(c.cancelled + ' cancelled', '#fee2e2', '#dc2626');
        h += '</span></div>';
    }
    h += '</div>';

    var missing = d.stripe_paid_carts ? d.stripe_paid_carts.filter(function(x) { return !x.has_cart_items; }) : [];
    if (missing.length > 0) {
        h += '<div style="border:1px solid #fcd34d;border-radius:8px;padding:16px;background:#fffbeb;margin-bottom:12px">';
        h += '<h3 style="font-weight:bold;color:#b45309;margin-bottom:8px">Paid Sessions Missing From Calendar (' + missing.length + ')</h3>';
        for (var j = 0; j < missing.length; j++) {
            var sc = missing[j];
            h += '<div style="font-size:13px;padding:2px 0">';
            h += '<strong>' + esc(sc.email || '?') + '</strong>';
            h += ' <span style="color:#6b7280;font-size:11px">cart:' + esc(sc.cart_id.slice(0,12)) + '</span>';
            if (sc.amount) h += ' <span style="font-size:11px">£' + (sc.amount / 100).toFixed(2) + '</span>';
            h += '</div>';
        }
        h += '</div>';
    } else {
        h += '<div style="border:1px solid #86efac;border-radius:8px;padding:16px;background:#f0fdf4;color:#15803d;font-size:14px">All paid Stripe sessions have matching cart items. Calendar and List are in sync.</div>';
    }

    if (d.orphaned_safety_forms && d.orphaned_safety_forms.length > 0) {
        h += '<div style="border:1px solid #fcd34d;border-radius:8px;padding:16px;background:#fffbeb;margin-top:12px">';
        h += '<h3 style="font-weight:bold;color:#b45309;margin-bottom:8px">Safety Forms Without Bookings (' + d.orphaned_safety_forms.length + ')</h3>';
        for (var k = 0; k < d.orphaned_safety_forms.length; k++) {
            var sf = d.orphaned_safety_forms[k];
            h += '<div style="font-size:13px">' + esc(sf.handler) + ' / ' + esc(sf.dog) + ' <span style="color:#6b7280;font-size:11px">cart:' + esc(sf.cart_id.slice(0,12)) + '</span></div>';
        }
        h += '</div>';
    }

    return h;
}

function formatRecov(r) {
    var h = '<div style="border:1px solid #e5e7eb;border-radius:8px;padding:16px">';
    h += '<h3 style="font-weight:bold;margin-bottom:12px">Recovery Results</h3>';
    h += '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px">';
    h += box(r.recovered_count, 'Recovered', '#f0fdf4');
    h += box(r.already_present_count, 'Already Present', '#f3f4f6');
    h += box(r.unrecoverable_count, 'Unrecoverable', '#fffbeb');
    h += '</div>';

    if (r.recovered) {
        for (var i = 0; i < r.recovered.length; i++) {
            var rec = r.recovered[i];
            h += '<div style="padding:6px;background:#f0fdf4;border-radius:4px;margin-bottom:4px;font-size:13px">';
            h += '<strong>' + esc(rec.email) + '</strong>';
            for (var j = 0; j < rec.items.length; j++) {
                var it = rec.items[j];
                h += '<div style="margin-left:12px;color:#15803d;font-size:12px">' + esc(it.date) + ' ' + esc(it.time) + ' — ' + esc(it.session) + ' — £' + it.price + '</div>';
            }
            h += '</div>';
        }
    }

    if (r.unrecoverable && r.unrecoverable.length > 0) {
        h += '<h4 style="font-weight:600;color:#b45309;margin-top:12px;margin-bottom:4px;font-size:14px">Could Not Recover:</h4>';
        for (var u = 0; u < r.unrecoverable.length; u++) {
            h += '<div style="font-size:13px;color:#b45309">' + esc(r.unrecoverable[u].email) + ' — ' + esc(r.unrecoverable[u].reason) + '</div>';
        }
    }

    h += '<p style="font-size:12px;color:#6b7280;margin-top:12px">Run the diagnostic again to verify.</p>';
    h += '</div>';
    return h;
}

function formatDedup(d) {
    var h = '<div style="border:1px solid #e5e7eb;border-radius:8px;padding:16px">';
    h += '<h3 style="font-weight:bold;margin-bottom:12px">Deduplication Results</h3>';
    h += '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px">';
    h += box(d.intra_cart_duplicates_removed, 'Within-Cart Dupes', '#fef2f2');
    h += box(d.cross_cart_duplicates_removed, 'Cross-Cart Dupes', '#fef2f2');
    h += box(d.total_removed, 'Total Removed', d.total_removed > 0 ? '#f0fdf4' : '#f3f4f6');
    h += '</div>';
    if (d.total_removed === 0) {
        h += '<div style="padding:12px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px;color:#15803d;font-size:14px">No duplicates found. All bookings are unique.</div>';
    }
    if (d.cross_cart_details && d.cross_cart_details.length > 0) {
        h += '<h4 style="font-weight:600;margin-top:12px;margin-bottom:4px;font-size:14px">Removed Duplicates:</h4>';
        for (var i = 0; i < d.cross_cart_details.length; i++) {
            var det = d.cross_cart_details[i];
            h += '<div style="font-size:13px;padding:4px;background:#fef2f2;border-radius:4px;margin-bottom:2px">';
            h += esc(det.handler || det.email || 'Unknown') + ' / ' + esc(det.dog || '?') + ' — ' + esc(det.slot);
            h += '</div>';
        }
    }
    h += '<p style="font-size:12px;color:#6b7280;margin-top:12px">Run the diagnostic to verify updated counts. Slot availability has been restored.</p>';
    h += '</div>';
    return h;
}

function box(val, label, bg) {
    return '<div style="background:' + bg + ';border-radius:8px;padding:12px;text-align:center"><p style="font-size:20px;font-weight:bold">' + val + '</p><p style="font-size:11px;color:#6b7280">' + label + '</p></div>';
}

function badge(text, bg, color) {
    return '<span style="display:inline-block;padding:1px 8px;border-radius:9999px;font-size:11px;background:' + bg + ';color:' + color + ';margin-left:4px">' + text + '</span>';
}

function esc(s) {
    if (!s) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
